/**
 * Procedural boiling/bubbling sound — synthesized via Web Audio, not an
 * audio asset. Avoids sourcing/hosting a sound file for one short looped
 * effect, and needs no network fetch. Randomized short "pop" bursts
 * (bandpass-filtered noise) at randomized intervals approximate a simmer.
 */
import { useEffect, useRef } from 'react';

export function useBoilingSound(active: boolean): void {
  const ctxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!active) return;

    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = ctxRef.current ?? new AudioCtx();
    ctxRef.current = ctx;
    if (ctx.state === 'suspended') void ctx.resume();

    let cancelled = false;

    function pop(): void {
      if (cancelled || !ctxRef.current) return;
      const audioCtx = ctxRef.current;
      const now = audioCtx.currentTime;
      const duration = 0.06 + Math.random() * 0.05;

      // Short burst of filtered noise — the "plip" of a bubble breaking the surface.
      const bufferSize = Math.floor(audioCtx.sampleRate * duration);
      const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);

      const noise = audioCtx.createBufferSource();
      noise.buffer = buffer;

      const filter = audioCtx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 180 + Math.random() * 260;
      filter.Q.value = 4;

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.22, now + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

      noise.connect(filter).connect(gain).connect(audioCtx.destination);
      noise.start(now);
      noise.stop(now + duration);

      timerRef.current = setTimeout(pop, 140 + Math.random() * 260);
    }

    pop();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [active]);

  // Tear down the AudioContext when the page unmounts entirely.
  useEffect(() => () => { void ctxRef.current?.close(); }, []);
}
